// apps/web/src/components/BarcodeScannerSheet.tsx
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { fetchProductFromOff } from "../lib/off";
import { useBarcodeScanner } from "../lib/useBarcodeScanner";
import { ScannerViewfinder } from "./ScannerViewfinder";
import { useTopmostEscape } from "../lib/useTopmostEscape";
import { useEdgeSwipe } from "../lib/useEdgeSwipe";
import type { NormalizedProduct } from "@shared/types";

interface Props {
  onMatch: (product: NormalizedProduct | null, code: string | null) => void;
  onClose: () => void;
}

const LOOKUP_TIMEOUT_MS = 3500;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<never>((_, rej) => setTimeout(() => rej(new Error("timeout")), ms)),
  ]);
}

export function BarcodeScannerSheet({ onMatch, onClose }: Props) {
  const t = useT();
  const [looking, setLooking] = useState(false);
  const handledRef = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useTopmostEscape(onClose);
  useEdgeSwipe(rootRef, { onCommit: onClose });

  const { videoRef, state, pause, retry, torchSupported, torchOn, toggleTorch } = useBarcodeScanner({
    onDecode: (code) => { void handle(code); },
  });

  async function handle(code: string) {
    if (handledRef.current) return;
    handledRef.current = true;
    pause();
    setLooking(true);
    api.barcodeRecordRecent(code).catch(() => { /* ignore */ });
    try {
      const cached = await withTimeout(api.barcodeLookup(code), LOOKUP_TIMEOUT_MS);
      if (cached.status === "hit")  { onMatch(cached.product, null); return; }
      if (cached.status === "miss") { onMatch(null, code); return; }
      const product = await fetchProductFromOff(code);
      api.barcodeCachePut(code, product).catch(() => { /* best-effort */ });
      onMatch(product, product ? null : code);
    } catch {
      onMatch(null, code);
    }
  }

  return createPortal(
    <div ref={rootRef} data-theme="dark" className="fixed inset-0 z-[60] bg-canvas text-fg flex flex-col">
      <div className="flex justify-between items-center px-4 pt-safe pt-4">
        <button onClick={onClose} aria-label={t("barcode.cancel")} className="text-fg-secondary text-sm py-2 px-1">
          ✕ {t("barcode.cancel")}
        </button>
        <button onClick={() => { onClose(); }} className="text-fg-secondary text-sm underline py-2">
          {t("barcode.use_ai_instead")}
        </button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {/* NOTE: do NOT add the `autoPlay` attribute — the hook calls play()
            explicitly; combining the two yields iOS Safari AbortError. */}
        <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" />
        {state.kind === "scanning" && !looking && (
          <ScannerViewfinder
            hint={t("barcode.aim_at_barcode")}
            torchSupported={torchSupported}
            torchOn={torchOn}
            onToggleTorch={() => void toggleTorch()}
          />
        )}
        {state.kind === "idle" && (
          <div className="absolute inset-0 flex items-center justify-center text-fg-secondary text-sm">
            {t("barcode.starting_camera")}
          </div>
        )}
        {looking && (
          <div className="absolute inset-0 flex items-center justify-center text-fg-secondary text-sm bg-black/30">
            {t("barcode.looking_up")}
          </div>
        )}
        {state.kind === "denied" && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-4 px-6 text-center">
            <p className="text-sm">{t("barcode.permission_denied")}</p>
            <div className="flex gap-2">
              <button onClick={retry} className="bg-surface-strong rounded-2xl px-4 py-2 text-sm">{t("barcode.try_again")}</button>
              <button onClick={onClose} className="bg-surface-strong rounded-2xl px-4 py-2 text-sm">{t("barcode.use_ai_instead")}</button>
            </div>
          </div>
        )}
        {state.kind === "no_camera" && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-4 text-sm">
            <p>{t("barcode.no_camera")}</p>
            <button onClick={onClose} className="bg-surface-strong rounded-2xl px-4 py-2">{t("barcode.use_ai_instead")}</button>
          </div>
        )}
        {state.kind === "error" && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-4 text-sm px-6 text-center">
            <p>{state.message}</p>
            <button onClick={retry} className="bg-surface-strong rounded-2xl px-4 py-2">{t("barcode.try_again")}</button>
          </div>
        )}
      </div>

    </div>,
    document.body,
  );
}
